import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { generateClient } from 'aws-amplify/api';
import { getUrl, uploadData } from 'aws-amplify/storage';
import { listBookOrders, listExchangeRates, listDocumentRequests, listEnrollments } from '../graphql/queries';
import { updateExchangeRate, createCourse } from '../graphql/mutations';
import { OrdersManager } from '../components/OrdersManager';
import InventoryManager from '../components/InventoryManager'; 
import DocumentRequestsManager from '../components/DocumentRequestsManager'; 
import CRMManager from '../components/CRMManager'; 
import CourseManager from '../components/CourseManager'; 
import EnrollmentManager from '../components/EnrollmentManager'; 
// QUIRÚRGICO: Usamos solo lo necesario de Recharts
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { 
    FaChartLine, FaBookOpen, FaUsers, FaFolderOpen, 
    FaSignOutAlt, FaChalkboardTeacher, FaUserGraduate, FaBrain, FaBell, FaTimes 
} from 'react-icons/fa';
import './AdminPanel.css'; 

const ADMIN_EMAILS = [
  "javierantonioolivo@gmail.com",
  "gioscarvaci@gmail.com",
  "giovanni.scarvaci@gmail.com",
  "giovanniscarvaciyasociados@gmail.com"
];

const AdminDashboard = ({ user, signOut }) => {
    const [orders, setOrders] = useState([]);
    const [docRequests, setDocRequests] = useState([]); 
    const [enrollments, setEnrollments] = useState([]);
    const [bcvRate, setBcvRate] = useState({ id: '', value: 0 });
    const [activeTab, setActiveTab] = useState('cerebro');
    const [loading, setLoading] = useState(false);
    const [courseLoading, setCourseLoading] = useState(false);

    // --- NUEVO: SISTEMA DE NOTIFICACIONES ---
    const [notifications, setNotifications] = useState([]);
    const [showNotifPanel, setShowNotifPanel] = useState(false);

    const addNotification = useCallback((title, message, type = 'info') => {
        const id = Date.now();
        setNotifications(prev => [{ id, title, message, type }, ...prev]);
        
        // Sonido opcional de notificación
        const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
        audio.play().catch(() => {});

        // Auto-eliminar después de 10 minutos para no saturar
        setTimeout(() => {
            setNotifications(prev => prev.filter(n => n.id !== id));
        }, 600000);
    }, []);

    const userEmail = (user?.signInDetails?.loginId || user?.attributes?.email || "").toLowerCase().trim();
    const isAdmin = ADMIN_EMAILS.includes(userEmail);

    const totalStats = useMemo(() => {
        const incomeBooks = orders
            .filter(o => o.status === "APROBADO" || o.status === "PAGADO") 
            .reduce((acc, curr) => acc + (Number(curr.amountUSD) || 0), 0);

        const incomeDocs = docRequests
            .filter(req => req.status === "COMPLETADO" || req.status === "PAGADO")
            .reduce((acc, curr) => acc + (Number(curr.priceUSD) || 0), 0);

        const incomeCourses = enrollments
            .filter(e => e.status === "APROBADO" || e.status === "ACTIVO" || e.status === "COMPLETADO")
            .reduce((acc, curr) => acc + (Number(curr.amountUSD) || 0), 0);
        
        return {
            books: incomeBooks,
            docs: incomeDocs,
            courses: incomeCourses,
            totalGlobal: incomeBooks + incomeDocs + incomeCourses
        };
    }, [orders, docRequests, enrollments]);

    const chartData = [
        { name: 'Libros', total: totalStats.books, color: '#D4AF37' },
        { name: 'Trámites', total: totalStats.docs, color: '#3498db' },
        { name: 'Cursos', total: totalStats.courses, color: '#2ecc71' },
    ];

    const handleVerComprobante = async (key) => {
        try {
            if (!key) { alert("Sin comprobante."); return; }
            const signedUrl = await getUrl({ 
                path: `public/${key}`,
                options: { expiresIn: 900 }
            });
            window.open(signedUrl.url.toString(), '_blank');
        } catch (error) {
            console.error(error);
            alert("Error al cargar imagen.");
        }
    };

    const handleSaveCourse = async (courseData, thumbnailFile) => {
        const client = generateClient();
        setCourseLoading(true);
        try {
            let thumbKey = "";
            if (thumbnailFile) {
                const cleanName = thumbnailFile.name.replace(/\s/g, "_");
                thumbKey = `courses/thumbs/${Date.now()}_${cleanName}`;
                await uploadData({
                    path: `public/${thumbKey}`,
                    data: thumbnailFile
                }).result;
            }

            await client.graphql({
                query: createCourse,
                variables: {
                    input: {
                        title: courseData.title,
                        subtitle: courseData.subtitle || "",
                        description: courseData.description,
                        priceUSD: parseFloat(courseData.priceUSD),
                        thumbnailUrl: thumbKey,
                        instructor: "Dr. Giovanni Scarvaci",
                        status: "ACTIVO"
                    }
                },
                authMode: 'userPool'
            });

            alert("✅ Curso creado exitosamente.");
            fetchAdminData();
            return true; 
        } catch (error) {
            console.error("Error al crear curso:", error);
            alert("Error al guardar curso.");
            return false;
        } finally {
            setCourseLoading(false);
        }
    };

    // MODIFICADO: Ahora detecta cambios para notificar
    const fetchAdminData = useCallback(async (isSilent = false) => {
        const client = generateClient();
        try {
            const [rateData, orderData, docsData, enrollmentData] = await Promise.all([
                client.graphql({ query: listExchangeRates, authMode: 'apiKey' }),
                client.graphql({ query: listBookOrders, authMode: 'userPool' }),
                client.graphql({ query: listDocumentRequests, authMode: 'userPool' }),
                client.graphql({ query: listEnrollments, authMode: 'userPool' })
            ]);

            const newOrders = orderData?.data?.listBookOrders?.items || [];
            const newDocs = docsData?.data?.listDocumentRequests?.items || [];
            const newEnrollments = enrollmentData?.data?.listEnrollments?.items || [];

            // Si es una actualización automática (silenciosa), comparamos para notificar
            if (isSilent) {
                if (newOrders.length > orders.length) {
                    addNotification("🛒 NUEVA VENTA", "Se ha registrado un nuevo pedido de libro.", "success");
                }
                if (newEnrollments.length > enrollments.length) {
                    addNotification("🎓 INSCRIPCIÓN", "Un nuevo alumno se ha inscrito en la academia.", "success");
                }
                if (newDocs.length > docRequests.length) {
                    addNotification("⚖️ TRÁMITE", "Nueva solicitud de trámite legal recibida.", "info");
                }
            }

            if (rateData?.data?.listExchangeRates?.items?.length > 0) {
                const r = rateData.data.listExchangeRates.items[0];
                setBcvRate({ id: r.id, value: r.value });
            }
            
            setOrders(newOrders);
            setDocRequests(newDocs);
            setEnrollments(newEnrollments);
        } catch (e) { 
            console.error("Error en la carga:", e);
        }
    }, [orders.length, docRequests.length, enrollments.length, addNotification]);

    useEffect(() => { 
        if(isAdmin) {
            fetchAdminData();
            // Polling cada 30 segundos para revisar si hay ventas nuevas
            const interval = setInterval(() => {
                fetchAdminData(true);
            }, 30000);
            return () => clearInterval(interval);
        } 
    }, [fetchAdminData, isAdmin]);

    const handleUpdateRate = async () => {
        const client = generateClient();
        try {
            setLoading(true);
            await client.graphql({
                query: updateExchangeRate,
                variables: { input: { id: bcvRate.id, value: parseFloat(bcvRate.value) } },
                authMode: 'userPool',
            });
            alert("✅ Tasa actualizada");
            fetchAdminData();
        } catch (e) { 
            alert(`Error de permisos.`); 
        } finally { setLoading(false); }
    };

    if (!isAdmin) {
        return (
            <div className="unauthorized-box" style={{textAlign: 'center', padding: '100px', background: '#000', color: 'white', height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
                <h2 style={{color: '#D4AF37'}}>Acceso no autorizado</h2>
                <button onClick={signOut} className="btn-save-pro">Cerrar Sesión</button>
            </div>
        );
    }

    return (
        <div className="admin-container">
            {/* --- INTERFAZ DE NOTIFICACIONES --- */}
            <div className="notif-bell-container" onClick={() => setShowNotifPanel(!showNotifPanel)} style={{position: 'fixed', top: '20px', right: '20px', zIndex: 1100, cursor: 'pointer', background: '#fff', padding: '10px', borderRadius: '50%', boxShadow: '0 2px 10px rgba(0,0,0,0.1)'}}>
                <FaBell size={22} color={notifications.length > 0 ? "#D4AF37" : "#333"} />
                {notifications.length > 0 && (
                    <span style={{position: 'absolute', top: '0', right: '0', background: 'red', color: 'white', borderRadius: '50%', padding: '2px 6px', fontSize: '10px', fontWeight: 'bold'}}>
                        {notifications.length}
                    </span>
                )}
            </div>

            {showNotifPanel && (
                <div className="notif-panel" style={{position: 'fixed', top: '75px', right: '20px', width: '320px', maxHeight: '450px', background: '#fff', boxShadow: '0 10px 30px rgba(0,0,0,0.15)', borderRadius: '15px', zIndex: 1100, overflowY: 'auto', padding: '15px', border: '1px solid #eee'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px'}}>
                        <h4 style={{margin: 0, color: '#333'}}>Notificaciones</h4>
                        <FaTimes onClick={() => setShowNotifPanel(false)} style={{cursor: 'pointer', color: '#999'}} />
                    </div>
                    {notifications.length === 0 ? (
                        <p style={{textAlign: 'center', color: '#999', fontSize: '14px'}}>No hay eventos nuevos</p>
                    ) : (
                        notifications.map(n => (
                            <div key={n.id} style={{padding: '12px', background: '#f8f9fa', borderRadius: '10px', marginBottom: '10px', borderLeft: `5px solid ${n.type === 'success' ? '#2ecc71' : '#3498db'}`}}>
                                <strong style={{display: 'block', fontSize: '12px', color: '#333'}}>{n.title}</strong>
                                <span style={{fontSize: '13px', color: '#666'}}>{n.message}</span>
                            </div>
                        ))
                    )}
                </div>
            )}

            <aside className="admin-sidebar">
                <div className="admin-logo-box">
                    <h3 style={{color: '#fff'}}>SCARVACI <span style={{color: '#D4AF37'}}>CLOUD</span></h3>
                    <p style={{fontSize: '11px', color: '#D4AF37'}}>{userEmail}</p>
                </div>
                <nav className="admin-menu">
                    <button className={activeTab === 'cerebro' ? 'active' : ''} onClick={() => setActiveTab('cerebro')}>
                        <FaBrain /> Cerebro de Ventas
                    </button>
                    <button className={activeTab === 'finanzas' ? 'active' : ''} onClick={() => setActiveTab('finanzas')}>
                        <FaChartLine /> Finanzas Globales
                    </button>
                    <button className={activeTab === 'documentos' ? 'active' : ''} onClick={() => setActiveTab('documentos')}>
                        <FaFolderOpen /> Trámites Legales
                    </button>
                    <button className={activeTab === 'catalogo' ? 'active' : ''} onClick={() => setActiveTab('catalogo')}>
                        <FaBookOpen /> Inventario Libros
                    </button>
                    <button className={activeTab === 'academia' ? 'active' : ''} onClick={() => setActiveTab('academia')}>
                        <FaChalkboardTeacher /> Gestionar Cursos
                    </button>
                    <button className={activeTab === 'inscripciones' ? 'active' : ''} onClick={() => setActiveTab('inscripciones')}>
                        <FaUserGraduate /> Control de Alumnos
                    </button>
                    <button className={activeTab === 'crm' ? 'active' : ''} onClick={() => setActiveTab('crm')}>
                        <FaUsers /> Clientes / CRM
                    </button>
                    <button onClick={signOut} className="logout-btn"><FaSignOutAlt /> Salir</button>
                </nav>
            </aside>

            <main className="admin-content" style={{background: '#f4f4f4'}}>
                {activeTab === 'cerebro' && (
                    <div className="fade-in">
                        <h2 style={{color: '#001f3f', marginBottom: '20px'}}>Cerebro de Ventas Inteligente</h2>
                        
                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px', marginBottom: '20px'}}>
                            <div className="admin-card" style={{background: '#fff', height: '400px', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
                                <h4 style={{color: '#333', marginBottom: '20px'}}>Distribución de Ingresos (USD)</h4>
                                <div style={{flex: 1, width: '100%'}}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={chartData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                            <XAxis dataKey="name" stroke="#333" />
                                            <YAxis stroke="#333" />
                                            <Tooltip contentStyle={{backgroundColor: '#fff', border: '1px solid #ddd', color: '#000'}} />
                                            <Bar dataKey="total">
                                                {chartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div className="admin-card" style={{background: '#fff', padding: '25px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', color: '#000'}}>
                                <h4 style={{color: '#001f3f', marginBottom: '20px'}}>Estado del Ecosistema</h4>
                                <div style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                                    <div style={{display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '10px'}}>
                                        <span style={{color: '#555'}}>📚 Libros Vendidos:</span>
                                        <span style={{fontWeight: 'bold'}}>{orders.length}</span>
                                    </div>
                                    <div style={{display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '10px'}}>
                                        <span style={{color: '#555'}}>⚖️ Trámites en Curso:</span>
                                        <span style={{fontWeight: 'bold'}}>{docRequests.length}</span>
                                    </div>
                                    <div style={{display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '10px'}}>
                                        <span style={{color: '#555'}}>🎓 Alumnos Inscritos:</span>
                                        <span style={{fontWeight: 'bold'}}>{enrollments.length}</span>
                                    </div>
                                    <div style={{marginTop: '20px', padding: '15px', background: '#f9f9f9', borderRadius: '8px', borderLeft: '4px solid #D4AF37'}}>
                                        <p style={{fontSize: '12px', color: '#666'}}>TOTAL VENTAS GLOBAL</p>
                                        <h2 style={{color: '#001f3f'}}>${totalStats.totalGlobal.toFixed(2)}</h2>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'finanzas' && (
                    <div className="fade-in">
                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px'}}>
                            <div className="admin-card" style={{borderLeft: '5px solid #D4AF37', background: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                                <span style={{color: '#666', fontSize: '12px'}}>LIBROS</span>
                                <h2 style={{color: '#333'}}>${totalStats.books.toFixed(2)}</h2>
                            </div>
                            <div style={{borderLeft: '5px solid #3498db', background: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}} className="admin-card">
                                <span style={{color: '#666', fontSize: '12px'}}>TRÁMITES</span>
                                <h2 style={{color: '#333'}}>${totalStats.docs.toFixed(2)}</h2>
                            </div>
                            <div style={{borderLeft: '5px solid #2ecc71', background: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}} className="admin-card">
                                <span style={{color: '#666', fontSize: '12px'}}>CURSOS</span>
                                <h2 style={{color: '#333'}}>${totalStats.courses.toFixed(2)}</h2>
                            </div>
                        </div>

                        <div className="admin-card" style={{marginBottom: '30px', background: '#fff'}}>
                            <h4 style={{color: '#333', marginBottom: '15px'}}>Ajuste Tasa BCV</h4>
                            <div style={{display: 'flex', gap: '15px', alignItems: 'center'}}>
                                <input type="number" className="pro-input" style={{width: '150px'}} value={bcvRate.value} onChange={(e) => setBcvRate({...bcvRate, value: e.target.value})} />
                                <button className="btn-save-pro" onClick={handleUpdateRate} disabled={loading}>{loading ? "..." : "Actualizar Tasa"}</button>
                            </div>
                        </div>

                        <div className="admin-card" style={{background: '#fff'}}>
                            <h3 style={{marginBottom: '20px', color: '#333'}}><FaBookOpen /> Auditoría de Ventas (Libros)</h3>
                            <OrdersManager orders={orders} onUpdate={fetchAdminData} onViewReceipt={handleVerComprobante} />
                        </div>
                    </div>
                )}

                {activeTab === 'documentos' && <div className="fade-in"><DocumentRequestsManager requests={docRequests} onUpdate={fetchAdminData} /></div>}
                {activeTab === 'catalogo' && <div className="fade-in"><InventoryManager /></div>}
                {activeTab === 'academia' && <div className="fade-in"><CourseManager onSaveCourse={handleSaveCourse} isSaving={courseLoading} /></div>}
                {activeTab === 'inscripciones' && <div className="fade-in"><EnrollmentManager enrollments={enrollments} onUpdate={fetchAdminData} /></div>}
                {activeTab === 'crm' && <div className="fade-in"><CRMManager orders={orders} docRequests={docRequests} /></div>}
            </main>
        </div>
    );
};

export default AdminDashboard;